import {
  NJButton,
  NJHeading,
  NJIconButton,
  NJInlineMessage,
  NJSelectItem,
  NJSelectRoot,
  NJSpinner,
  NJTag,
  NJText,
} from "@engie-group/fluid-design-system-react";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BackButton } from "@/components/BackButton";
import { ConfirmModal } from "@/components/ConfirmModal";
import { requiredLabel } from "@/components/form/validation";
import { setImpersonatedUserId } from "@/features/auth/impersonation";
import { useAdminGroups } from "./useAdminGroups";
import {
  useAddGroupToUser,
  useAdminUser,
  useDeleteAdminUser,
  useRemoveGroupFromUser,
} from "./useAdminUsers";

/** Admin user detail: assign/remove groups, impersonate, delete (US-10). */
export function AdminUserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: user, isLoading, isError } = useAdminUser(userId);
  const { data: groupsPage } = useAdminGroups({ limit: 100 });
  const addGroup = useAddGroupToUser();
  const removeGroup = useRemoveGroupFromUser();
  const deleteUser = useDeleteAdminUser();

  const [groupToAssign, setGroupToAssign] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const assignableGroups = useMemo(() => {
    const assigned = new Set(user?.groups ?? []);
    return (groupsPage?.items ?? [])
      .map((group) => group.name)
      .filter((name) => !assigned.has(name));
  }, [groupsPage, user]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <NJSpinner aria-label="Loading user" />
      </div>
    );
  }

  if (isError || !user) {
    return (
      <div className="flex flex-col gap-4">
        <BackButton to="/admin/users" label="Back to Users" />
        <NJInlineMessage variant="error">
          Could not load this user.
        </NJInlineMessage>
      </div>
    );
  }

  const fullName = `${user.firstName} ${user.lastName}`.trim();

  const impersonate = () => {
    setImpersonatedUserId(user.id);
    void queryClient.invalidateQueries();
    navigate("/");
  };

  return (
    <div className="flex flex-col gap-6">
      <BackButton to="/admin/users" label="Back to Users" />

      <NJHeading as="h1" scale="lg">
        {fullName || user.login || user.email}
      </NJHeading>

      <section className="grid gap-4 border border-nj-neutral-subtle-default p-6 sm:grid-cols-2">
        <Field label="Login" value={user.login ?? "-"} />
        <Field label="Email" value={user.email} />
        <Field label="Name" value={fullName || "-"} />
        <Field
          label="Last login"
          value={
            user.lastLoginAt
              ? new Date(user.lastLoginAt).toLocaleString()
              : "Never"
          }
        />
      </section>

      <section className="flex flex-col gap-3 border border-nj-neutral-subtle-default p-6">
        <NJHeading as="h2" scale="sm">
          Groups
        </NJHeading>
        <div className="flex flex-wrap items-center gap-2">
          {(user.groups ?? []).length === 0 ? (
            <NJText
              as="span"
              scale="sm"
              className="text-nj-neutral-tertiary-default"
            >
              No group assigned.
            </NJText>
          ) : (
            (user.groups ?? []).map((group) => (
              <span
                key={group}
                className="inline-flex items-center gap-1 rounded-full border border-nj-neutral-subtle-default py-0.5 pl-3 pr-1"
              >
                <NJTag label={group} variant="grey" size="sm" />
                <NJIconButton
                  icon="close"
                  variant="tertiary"
                  scale="sm"
                  label={`Remove from ${group}`}
                  isDisabled={removeGroup.isPending}
                  onClick={() =>
                    removeGroup.mutate({ userId: user.id, groupName: group })
                  }
                />
              </span>
            ))
          )}
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div className="w-full">
            <NJSelectRoot
              id="assign-group"
              label={requiredLabel("Group to assign")}
              value={groupToAssign}
              listNavigationLabel="Use the arrows and Enter to choose a group"
              buttonDefaultValueLabel="Choose a group"
              onChange={(value) => setGroupToAssign(value ?? "")}
            >
              {assignableGroups.map((name) => (
                <NJSelectItem key={name} value={name}>
                  {name}
                </NJSelectItem>
              ))}
            </NJSelectRoot>
          </div>
          <NJButton
            label="Assign group"
            disabled={!groupToAssign || addGroup.isPending}
            onClick={() => {
              addGroup.mutate(
                { userId: user.id, groupName: groupToAssign },
                { onSuccess: () => setGroupToAssign("") },
              );
            }}
          />
        </div>
      </section>

      <section className="flex flex-wrap gap-3">
        <NJButton
          label="Impersonate"
          variant="secondary"
          icon="record_voice_over"
          onClick={impersonate}
        />
        <NJButton
          label="Delete user"
          variant="destructive"
          emphasis="subtle"
          disabled={deleteUser.isPending}
          onClick={() => setConfirmOpen(true)}
        />
      </section>

      <ConfirmModal
        open={confirmOpen}
        setOpen={setConfirmOpen}
        title="Delete user"
        isPending={deleteUser.isPending}
        onConfirm={() =>
          deleteUser.mutate(user.id, {
            onSuccess: () => navigate("/admin/users"),
          })
        }
      >
        Delete user <strong>{user.email}</strong>? This action cannot be undone.
      </ConfirmModal>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <NJText
        as="span"
        scale="md"
        className="font-nj-bold text-nj-neutral-primary-default"
      >
        {label}
      </NJText>
      <NJText
        as="span"
        scale="xs"
        className="text-nj-neutral-secondary-default"
      >
        {value}
      </NJText>
    </div>
  );
}
